import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Shirt,
  Package,
  User,
  Search,
  Calendar,
  X,
  Truck,
  Printer,
  Pencil,
  Minus,
  Plus,
  Save,
  AlertTriangle,
} from 'lucide-react';
import { getPrinterInstance } from '@/utils/bluetoothPrinter';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { fetchEntregasClientes, updateEntregaCliente } from '@/services/entregasClientes.service';
import { getClientAssignedGarments, getClientGarments } from '@/pages/parametrizacion/clientGarments/Services/clientGarments.services';

const RecoleccionesCompletadas = ({ idLavanderia, onCountChange }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [recolecciones, setRecolecciones] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecoleccion, setSelectedRecoleccion] = useState(null);

  useEffect(() => {
    loadRecolecciones();
  }, [idLavanderia]);

  const loadRecolecciones = async () => {
    setIsLoading(true);
    try {
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);

      const query = {
        'lavanderia.id': idLavanderia,
        tipo: 'Recoleccion',
        createdAt: { $gte: hoy.toISOString() },
      };

      const rolLower = String(user?.role ?? user?.rol ?? '').toLowerCase();
      if (rolLower === 'conductor') {
        query['conductor.id'] = user?.id || user?.raw?._id;
      }

      const response = await fetchEntregasClientes({
        query,
        limit: 100,
        page: 1,
      });
      setRecolecciones(response.data || []);
      onCountChange?.(response.totalRecords || 0);
    } catch (error) {
      console.error('Error al cargar recolecciones:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las recolecciones',
        variant: 'destructive',
      });
      setRecolecciones([]);
      onCountChange?.(0);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredRecolecciones = recolecciones.filter((r) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      r.cliente?.nombre?.toLowerCase().includes(term) ||
      r.consecutivo?.toLowerCase().includes(term) ||
      r.conductor?.nombre?.toLowerCase().includes(term)
    );
  });

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getEstadoBadge = (estado) => {
    const estilos = {
      'Entregado': 'bg-red-100 text-red-800 border border-red-300',
      'Pendiente Verificacion': 'bg-green-100 text-green-800 border border-green-300',
      'Verificado': 'bg-emerald-100 text-emerald-800',
      'Cancelado': 'bg-gray-100 text-gray-800',
    };
    return estilos[estado] || 'bg-gray-100 text-gray-800';
  };

  const getEstadoLabel = (estado) => {
    const labels = {
      'Entregado': '⚠ Pendiente entregar a lavandería',
      'Pendiente Verificacion': '✓ Entregado en lavandería',
      'Verificado': '✓ Verificado',
      'Cancelado': 'Cancelado',
    };
    return labels[estado] || estado;
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto" />
        <p className="text-sm text-gray-500 mt-4">Cargando recolecciones...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Búsqueda */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar por cliente, consecutivo o conductor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 text-sm h-10"
              />
            </div>
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {filteredRecolecciones.length} recolectada(s) hoy
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      {filteredRecolecciones.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Shirt className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              No hay recolecciones hoy
            </h3>
            <p className="text-sm text-gray-500">
              Las recolecciones completadas aparecerán aquí
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-2">
          {filteredRecolecciones.map((rec) => {
            const totalPrendas = rec.items?.reduce((s, i) => s + (i.cantidad_confirmada || i.cantidad_alistada || 0), 0) || 0;

            return (
              <motion.div
                key={rec._id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card
                  className="border-l-4 border-l-orange-400 cursor-pointer hover:shadow-md hover:border-orange-400 active:scale-[0.98] transition-all"
                  onClick={() => setSelectedRecoleccion(rec)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm text-orange-700">{rec.consecutivo}</span>
                      <Badge className={`text-[10px] px-1.5 py-0 ${getEstadoBadge(rec.estado)}`}>
                        {getEstadoLabel(rec.estado)}
                      </Badge>
                    </div>
                    <p className="font-medium text-sm text-gray-900 truncate">{rec.cliente?.nombre}</p>
                    <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Package className="h-3 w-3" />{totalPrendas} prendas
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />{formatTime(rec.fecha_recoleccion || rec.fecha_creacion)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Truck className="h-3 w-3" />{rec.conductor?.nombre || 'Sin conductor'}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Modal detalle fullscreen */}
      <Dialog open={!!selectedRecoleccion} onOpenChange={() => setSelectedRecoleccion(null)}>
        <DialogContent className="!fixed !inset-0 !left-0 !top-0 !translate-x-0 !translate-y-0 !max-w-none !w-full !h-full !rounded-none !p-0 !gap-0 !border-0 flex flex-col [&>button]:hidden">
          <DialogTitle className="sr-only">Detalle de recolección</DialogTitle>
          {selectedRecoleccion && (
            <RecoleccionDetailView
              recoleccion={selectedRecoleccion}
              onClose={() => setSelectedRecoleccion(null)}
              formatDateTime={formatDateTime}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const RecoleccionDetailView = ({ recoleccion: recoleccionInicial, onClose, formatDateTime }) => {
  const { toast } = useToast();
  const [recoleccion, setRecoleccion] = useState(recoleccionInicial);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingPrendas, setIsLoadingPrendas] = useState(false);
  const [familiaMap, setFamiliaMap] = useState(new Map());
  // Estado del formulario de edición
  const [editItems, setEditItems] = useState([]);
  const [editObservaciones, setEditObservaciones] = useState('');
  const printer = getPrinterInstance();

  // Cargar familias de prendas una sola vez al abrir
  useEffect(() => {
    getClientGarments({ limit: 500 }).then((res) => {
      const fMap = new Map();
      (res.data || []).forEach((g) => fMap.set(String(g._id), g.familia || null));
      setFamiliaMap(fMap);
    }).catch(() => {});
  }, []);

  const totalPrendas = recoleccion.items?.reduce((s, i) => s + (i.cantidad_alistada || 0), 0) || 0;

  // ── Entrar a modo edición ──
  const handleStartEdit = async () => {
    setIsLoadingPrendas(true);
    try {
      const response = await getClientAssignedGarments(recoleccion.cliente?.id || recoleccion.cliente?._id);
      const garments = Array.isArray(response.data) ? response.data : [];

      // Mapa actual de cantidades en la recolección
      const cantMap = {};
      (recoleccion.items || []).forEach((i) => {
        cantMap[i.prendaId] = i.cantidad_alistada || 0;
      });

      // Mezclar prendas asignadas al cliente con cantidades actuales
      const merged = garments.map((g) => {
        const prendaId = g.prendaId || g.tipoPrenda?._id || g.tipoPrenda?.id || g._id;
        return {
          prendaId,
          nombre: g.tipoPrenda?.nombre || g.nombre || 'Prenda',
          peso: g.peso || 0,
          cantidad: cantMap[prendaId] ?? 0,
        };
      });

      // Si la recolección tiene prendas que ya no están en la lista del cliente, igual mostrarlas
      (recoleccion.items || []).forEach((i) => {
        if (!merged.find((m) => m.prendaId === i.prendaId)) {
          merged.push({
            prendaId: i.prendaId,
            nombre: i.nombre,
            peso: i.peso || 0,
            cantidad: i.cantidad_alistada || 0,
          });
        }
      });

      setEditItems(merged);
      setEditObservaciones(recoleccion.observaciones || '');
      setIsEditing(true);
    } catch {
      toast({ title: 'Error', description: 'No se pudieron cargar las prendas del cliente', variant: 'destructive' });
    } finally {
      setIsLoadingPrendas(false);
    }
  };

  const handleCantidad = (prendaId, delta) => {
    setEditItems((prev) =>
      prev.map((i) => i.prendaId === prendaId
        ? { ...i, cantidad: Math.max(0, i.cantidad + delta) }
        : i
      )
    );
  };

  const handleCantidadDirecta = (prendaId, val) => {
    const n = parseInt(val, 10);
    setEditItems((prev) =>
      prev.map((i) => i.prendaId === prendaId ? { ...i, cantidad: isNaN(n) || n < 0 ? 0 : n } : i)
    );
  };

  // ── Guardar cambios ──
  const handleSave = async () => {
    const itemsActivos = editItems.filter((i) => i.cantidad > 0).map((i) => ({
      prendaId: i.prendaId,
      nombre: i.nombre,
      peso: i.peso,
      cantidad_alistada: i.cantidad,
      cantidad_confirmada: i.cantidad,
    }));

    if (itemsActivos.length === 0) {
      toast({ title: 'Sin prendas', description: 'Debes registrar al menos una prenda', variant: 'destructive' });
      return;
    }

    setIsSaving(true);
    try {
      const updated = await updateEntregaCliente(recoleccion._id, {
        items: itemsActivos,
        observaciones: editObservaciones,
      });
      const dataActualizada = updated.data || updated;
      setRecoleccion((prev) => ({ ...prev, ...dataActualizada, items: itemsActivos, observaciones: editObservaciones }));
      setIsEditing(false);
      toast({ title: 'Guardado', description: 'Recolección actualizada correctamente' });
    } catch (e) {
      toast({ title: 'Error al guardar', description: e.message, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  // ── Imprimir ──
  const handlePrint = async () => {
    if (!printer.isBluetoothAvailable()) {
      toast({ title: 'No disponible', description: 'Tu navegador no soporta Bluetooth', variant: 'destructive' });
      return;
    }
    setIsPrinting(true);
    try {
      if (!printer.isConnected()) {
        toast({ title: 'Conectando...', description: 'Selecciona tu impresora Bluetooth' });
        await printer.connect();
      }
      toast({ title: 'Imprimiendo...', description: 'Generando 2 copias' });
      const enrichedItems = (recoleccion.items || []).map((item) => ({
        ...item,
        familia: familiaMap.get(String(item.prendaId)) || null,
      }));
      await printer.printRecoleccion({ ...recoleccion, items: enrichedItems });
      toast({ title: 'Listo', description: `Etiquetas ${recoleccion.consecutivo} impresas` });
    } catch (error) {
      const raw = (error?.message || '').toLowerCase();
      const msg = raw.includes('user cancelled') || raw.includes('user denied')
        ? 'Impresión cancelada.'
        : raw.includes('no se pudo conectar') || raw.includes('no hay conexión')
        ? 'No se pudo conectar. Verifica que la impresora esté encendida y cerca.'
        : raw.includes('bluetooth')
        ? 'Error Bluetooth: ' + error.message
        : error.message || 'No se pudo imprimir. Intenta de nuevo.';
      toast({ title: 'Error de impresión', description: msg, variant: 'destructive' });
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className={`${isEditing ? 'bg-indigo-600' : 'bg-orange-500'} text-white p-4 flex items-center justify-between shrink-0 transition-colors`}>
        <div>
          <h2 className="text-lg font-bold">{recoleccion.consecutivo}</h2>
          <p className="text-sm opacity-80">{isEditing ? 'Modo edición' : recoleccion.cliente?.nombre}</p>
        </div>
        <Button size="icon" variant="ghost" className="text-white hover:bg-white/20 h-8 w-8" onClick={isEditing ? () => setIsEditing(false) : onClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      {isEditing ? (
        /* ─── MODO EDICIÓN ─── */
        <>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {recoleccion.estado === 'Verificado' && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-amber-800 text-sm">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>Esta recolección ya fue <strong>verificada</strong>. Al guardar se actualizarán los datos.</span>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Prendas recolectadas</p>
              <div className="space-y-2">
                {editItems.map((item) => (
                  <div key={item.prendaId} className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${item.cantidad > 0 ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'}`}>
                    <span className={`flex-1 text-sm font-medium truncate ${item.cantidad > 0 ? 'text-orange-900' : 'text-gray-400'}`}>
                      {item.nombre}
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => handleCantidad(item.prendaId, -1)}
                        className="w-8 h-8 rounded-full bg-white border border-gray-300 flex items-center justify-center hover:bg-gray-100 active:scale-95 transition-all"
                      >
                        <Minus className="h-3.5 w-3.5 text-gray-600" />
                      </button>
                      <input
                        type="number"
                        min="0"
                        value={item.cantidad}
                        onChange={(e) => handleCantidadDirecta(item.prendaId, e.target.value)}
                        className="w-14 text-center text-base font-bold border border-gray-300 rounded-lg py-1 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      />
                      <button
                        onClick={() => handleCantidad(item.prendaId, 1)}
                        className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center hover:bg-orange-600 active:scale-95 transition-all"
                      >
                        <Plus className="h-3.5 w-3.5 text-white" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Observaciones</p>
              <textarea
                value={editObservaciones}
                onChange={(e) => setEditObservaciones(e.target.value)}
                rows={3}
                placeholder="Observaciones opcionales..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
              />
            </div>

            {/* Resumen edición */}
            <div className="bg-indigo-50 rounded-xl p-3 flex items-center gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-indigo-700">
                  {editItems.reduce((s, i) => s + i.cantidad, 0)}
                </p>
                <p className="text-xs text-indigo-500">Total prendas</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-500">
                  {editItems.filter((i) => i.cantidad > 0).length}
                </p>
                <p className="text-xs text-gray-400">Tipos incluidos</p>
              </div>
            </div>
          </div>

          <div className="shrink-0 border-t p-4 flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setIsEditing(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {isSaving
                ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Guardando...</>
                : <><Save className="h-4 w-4" />Guardar cambios</>}
            </Button>
          </div>
        </>
      ) : (
        /* ─── MODO VISTA ─── */
        <>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Info general */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Tipo</p>
                <Badge className="bg-orange-100 text-orange-800">Recolección</Badge>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Lavandería</p>
                <p className="text-sm font-medium">{recoleccion.lavanderia?.nombre || '-'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Conductor</p>
                <div className="flex items-center gap-1">
                  <Truck className="h-3.5 w-3.5 text-gray-400" />
                  <p className="text-sm font-medium">{recoleccion.conductor?.nombre || '-'}</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Entregó</p>
                <div className="flex items-center gap-1">
                  <User className="h-3.5 w-3.5 text-gray-400" />
                  <p className="text-sm font-medium">{recoleccion.recibe?.nombre || '-'}</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Fecha recolección</p>
                <p className="text-sm font-medium">{formatDateTime(recoleccion.fecha_recoleccion)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Cliente NIT</p>
                <p className="text-sm font-medium">{recoleccion.cliente?.nit || '-'}</p>
              </div>
            </div>

            {/* Resumen */}
            <div className="flex items-center gap-4 bg-orange-50 rounded-lg p-3">
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-700">{totalPrendas}</p>
                <p className="text-xs text-orange-600">Prendas recolectadas</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-500">{(recoleccion.items || []).length}</p>
                <p className="text-xs text-gray-500">Tipos de prenda</p>
              </div>
            </div>

            {/* Items agrupados por familia */}
            {(() => {
              const itemsConFamilia = (recoleccion.items || []).map((item) => ({
                ...item,
                familia: familiaMap.get(String(item.prendaId)) || null,
              }));
              const groups = new Map();
              itemsConFamilia.forEach((item) => {
                const key = item.familia?.nombre || 'Sin Familia';
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key).push(item);
              });
              const sorted = Array.from(groups.entries()).sort(([a], [b]) => {
                if (a === 'Sin Familia') return 1;
                if (b === 'Sin Familia') return -1;
                return a.localeCompare(b);
              });
              return (
                <div>
                  <h3 className="font-semibold text-sm text-gray-700 mb-2">
                    Detalle de prendas ({(recoleccion.items || []).length})
                  </h3>
                  <div className="space-y-3">
                    {sorted.map(([familiaName, items]) => {
                      const subtotal = items.reduce((s, i) => s + (i.cantidad_alistada || 0), 0);
                      return (
                        <div key={familiaName}>
                          <div className="flex items-center justify-between px-2 py-1 bg-gray-200 rounded-md mb-1">
                            <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">{familiaName}</span>
                            <span className="text-xs font-bold text-gray-600">{subtotal} uds</span>
                          </div>
                          <div className="space-y-1 pl-2">
                            {items.map((item, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm p-2.5 bg-gray-50 rounded-lg">
                                <span className="truncate flex-1 font-medium">{item.nombre}</span>
                                <span className="font-semibold text-orange-700 shrink-0 ml-2">
                                  {item.cantidad_alistada || 0}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Observaciones */}
            {recoleccion.observaciones && (
              <div className="bg-yellow-50 rounded-lg p-3">
                <p className="text-xs text-yellow-700 font-semibold mb-1">Observaciones</p>
                <p className="text-sm text-yellow-800">{recoleccion.observaciones}</p>
              </div>
            )}

            {/* Estado verificación */}
            {recoleccion.estado === 'Verificado' && (
              <div className="bg-green-50 rounded-lg p-3">
                <p className="text-xs text-green-700 font-semibold mb-1">Verificado</p>
                <p className="text-sm text-green-800">
                  {recoleccion.verificado_por?.nombre || '-'} - {formatDateTime(recoleccion.fecha_verificacion)}
                </p>
                {recoleccion.observacion_verificacion && (
                  <p className="text-xs text-green-600 mt-1">{recoleccion.observacion_verificacion}</p>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="shrink-0 border-t p-4 flex gap-2">
            {recoleccion.estado === 'Entregado' && (
              <Button
                variant="outline"
                onClick={handleStartEdit}
                disabled={isLoadingPrendas}
                className="gap-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              >
                {isLoadingPrendas
                  ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600" />
                  : <Pencil className="h-4 w-4" />}
                Editar
              </Button>
            )}
            <Button
              onClick={handlePrint}
              disabled={isPrinting}
              className="flex-1 gap-2 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isPrinting
                ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Imprimiendo...</>
                : <><Printer className="h-4 w-4" />Imprimir etiquetas (2 copias)</>}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default RecoleccionesCompletadas;
